'use client';

import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { connectAndJoinCall, streamJoinErrorMessage } from '@src/components/sco/Advisors/sessions/streamSessionJoin';
import { Call, CallControls, SpeakerLayout, StreamCall, StreamTheme, StreamVideo, StreamVideoClient } from '@stream-io/video-react-sdk';
import { useEffect, useRef, useState } from 'react';
import '@stream-io/video-react-sdk/dist/css/styles.css';
export type StreamJoinCreds = {
  apiKey: string;
  token: string;
  userId: string;
  userName?: string | null;
  callType: string;
  callId: string;
};

/**
 * Live Stream Video room. Parent supplies server-minted join credentials.
 *
 * Client creation lives in the join effect (not useMemo) so React Strict Mode
 * cleanup cannot disconnectUser() on an instance that a later join still uses.
 * connectUser is awaited before call.join().
 */
export const StreamSessionRoom = ({
  credentials,
  onJoined,
  onLeft
}: {
  credentials: StreamJoinCreds;
  onJoined?: () => void;
  onLeft?: () => void;
}) => {
  const [session, setSession] = useState<{
    client: StreamVideoClient;
    call: Call;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const onJoinedRef = useRef(onJoined);
  useEffect(() => {
    onJoinedRef.current = onJoined;
  }, [onJoined]);
  useEffect(() => {
    const live: {
      cancelled: boolean;
      client: StreamVideoClient | null;
      call: Call | null;
    } = {
      cancelled: false,
      client: null,
      call: null
    };
    const run = async () => {
      setError(null);
      setSession(null);
      const streamClient = new StreamVideoClient({
        apiKey: credentials.apiKey
      });
      live.client = streamClient;
      try {
        const result = await connectAndJoinCall({
          connectUser: async () => {
            await streamClient.connectUser({
              id: credentials.userId,
              name: credentials.userName || credentials.userId
            }, credentials.token);
          },
          join: async () => {
            const nextCall = streamClient.call(credentials.callType, credentials.callId);
            await nextCall.join({
              create: false
            });
            live.call = nextCall;
          },
          leave: async () => {
            await live.call?.leave();
            live.call = null;
          },
          isCancelled: () => live.cancelled
        });
        if (live.cancelled || result === 'cancelled' || !live.client || !live.call) {
          await live.call?.leave().catch(() => null);
          await live.client?.disconnectUser().catch(() => null);
          return;
        }
        setSession({
          client: live.client,
          call: live.call
        });
        onJoinedRef.current?.();
      } catch (err) {
        if (live.cancelled) {
          await live.call?.leave().catch(() => null);
          await live.client?.disconnectUser().catch(() => null);
          return;
        }
        setError(streamJoinErrorMessage(err));
      }
    };
    void run();
    return () => {
      live.cancelled = true;
      void (async () => {
        await live.call?.leave().catch(() => null);
        await live.client?.disconnectUser().catch(() => null);
      })();
    };
  }, [attempt, credentials.apiKey, credentials.token, credentials.userId, credentials.userName, credentials.callId, credentials.callType]);
  if (error) {
    return <VStack align={'stretch'} gap={2} padding={4}>
        <Text color={'red.fg'} fontSize={'sm'}>
          {error}
        </Text>
        <HStack gap={2} wrap={'wrap'}>
          <Button size={'sm'} variant={'outline'} onClick={() => {
          setError(null);
          setAttempt(n => n + 1);
        }}>
            Retry
          </Button>
          {onLeft && <Button size={'sm'} variant={'outline'} onClick={onLeft}>
              Leave
            </Button>}
        </HStack>
      </VStack>;
  }
  if (!session) {
    return <Text color={'colorPalette.fg/60'} fontSize={'sm'} padding={4}>
        Connecting to Stream…
      </Text>;
  }
  return <Box bg={'bg.stage'} borderColor={'colorPalette.border/40'} borderRadius={'lg'} borderWidth={'1px'} height={'full'} minHeight={'420px'} overflow={'hidden'}>
      <StreamVideo client={session.client}>
        <StreamCall call={session.call}>
          <StreamTheme>
            <SpeakerLayout />
            <CallControls onLeave={onLeft} />
          </StreamTheme>
        </StreamCall>
      </StreamVideo>
    </Box>;
};